import { Component, OnInit, ViewChild } from '@angular/core';
import { ProfileStatus } from './profile-status.model';
import { ProfileStatusService } from './profile-status.service';
import { UtilService } from '../../shared/util/util.service';
import { NotificationService } from '../../shared/notification/notification.service';
import { TranslationService } from '../../shared/translation/translation.service';
import { DialogService, DatagridService } from '../../shared/index';
import { AsyncState } from '../../shared/model/index';
import { ProfileRead } from '../../profiles/shared/profile-read.model';

@Component({
  selector: 'wl-profile-status-edit',
  templateUrl: 'profile-status.component.html',
  styleUrls: ['profile-status.component.css'],
    standalone: false
})
export class ProfileStatusComponent implements OnInit {
  public _utilService: UtilService;
  public _profileStatusService: ProfileStatusService;
  public _notifyService: NotificationService;
  public _translationService: TranslationService;
  public _dataGridService: DatagridService;
  public _profileStatusToDelete: number;

  public isSearching: boolean;
  public loadingProfileStatus: AsyncState;

  public saving: boolean;
  public savingError: string;

  public loadingProfileStatuses: boolean;
  public loadingProfileStatusError: string;

  public deleting: boolean;
  public deleteError: string;

  public selectedProfileStatus: ProfileStatus;

  public profileStatus: ProfileStatus;
  public profileStatuses: Array<ProfileStatus>;
  public profileStatusToMigrateTo: string;

  public searchResult: any;
  public searchParams: any;

  public changeProfiles: any;

  public creatingNewStatus: boolean;
  public showStatusModal = false;
  public showDeleteModal = false;

  public constructor(
    utilService: UtilService,
    profileStatusService: ProfileStatusService,
    notifyService: NotificationService,
    translationService: TranslationService,
    dataGridService: DatagridService
  ) {
    this._profileStatusService = profileStatusService;
    this._utilService = utilService;
    this._notifyService = notifyService;
    this._translationService = translationService;
    this._dataGridService = dataGridService;
    this._profileStatusToDelete = 0;

    this.loadingProfileStatuses = false;
    this.loadingProfileStatusError = '';

    this.isSearching = false;

    this.changeProfiles = false;

    this.profileStatusToMigrateTo = 'None';

    this.profileStatus = new ProfileStatus();
    this.profileStatuses = new Array<ProfileStatus>();

    this.deleting = false;
    this.deleteError = '';

    this.searchResult = {};

    this.loadingProfileStatus = new AsyncState();

    this.saving = false;
    this.savingError = '';
    this.selectedProfileStatus = new ProfileStatus();

    this.searchParams = { Keyword: '', PageSize: 10, OrderBy: 'Id' };

    this.creatingNewStatus = false;
  }

  ngOnInit() {}

  public lazyLoadProfileStatuses = ($event: any): void => {
    this._dataGridService.mapDatagridPagingToPagedSearch($event, this.searchParams);

    this.loadProfileStatuses(this.searchParams);
  };

  public newProfileStatus = (): void => {
    this.saving = false;
    this.savingError = '';
    this.creatingNewStatus = true;

    this.loadingProfileStatus.busy = false;
    this.loadingProfileStatus.message = '';

    let newProfileStatus: ProfileStatus = new ProfileStatus();
    this.selectedProfileStatus = newProfileStatus;
    this.showStatusModal = true;
  };

  public loadProfileStatuses(searchParams: any) {
    this.loadingProfileStatuses = true;
    this.loadingProfileStatusError = '';

    this._profileStatusService
      .getProfileStatuses(searchParams)
      .then((data: any) => {
        this.loadingProfileStatuses = false;
        this.profileStatuses = data.Result;
        this.searchResult = data;
        if (this.profileStatuses.length > 0) {
          this.selectedProfileStatus = this.profileStatuses[0];
        }
      })
      .catch(error => {
        this.loadingProfileStatuses = false;
        this.loadingProfileStatusError = this._utilService.extractErrorMessage(error);
      });
  }

  public saveProfileStatuses = (): void => {
    this.saving = true;
    this.savingError = '';

    if (this.selectedProfileStatus.ID > 0) {
      if (this.changeProfiles) {
        this._profileStatusService
          .updateProfileStatusAndProfiles(this.selectedProfileStatus)
          .then(() => {
            this.saving = false;

            this.loadProfileStatuses(this.searchParams);
            this.showStatusModal = false;

            this._translationService.get('common.success').then(translation => this._notifyService.success(translation));
          })
          .catch(error => {
            this.saving = false;
            this.savingError = this._utilService.extractErrorMessage(error);
          });
      } else {
        this._profileStatusService
          .updateProfileStatus(this.selectedProfileStatus)
          .then(() => {
            this.saving = false;

            this.loadProfileStatuses(this.searchParams);
            this.showStatusModal = false;

            this._translationService.get('common.success').then(translation => this._notifyService.success(translation));
          })
          .catch(error => {
            this.saving = false;
            this.savingError = this._utilService.extractErrorMessage(error);
          });
      }
    } else {
      this._profileStatusService
        .insertProfileStatus(this.selectedProfileStatus)
        .then(() => {
          this.saving = false;

          this.loadProfileStatuses(this.searchParams);
          this.showStatusModal = false;

          this._translationService.get('common.success').then(translation => this._notifyService.success(translation));
        })
        .catch(error => {
          this.saving = false;
          this.savingError = this._utilService.extractErrorMessage(error);
        });
    }
  };

  public editProfileStatus = (id: number): void => {
    this.saving = false;
    this.savingError = '';
    this.creatingNewStatus = false;

    this.loadingProfileStatus.busy = true;
    this.loadingProfileStatus.message = '';
    this.loadingProfileStatus.success = false;

    this.changeProfiles = false;

    this._profileStatusService
      .getProfileStatus(id)
      .then(data => {
        this.loadingProfileStatus.busy = false;
        this.loadingProfileStatus.success = true;
        this.selectedProfileStatus = data;
        this.showStatusModal = true;
      })
      .catch(error => {
        this.loadingProfileStatus.busy = false;
        this.loadingProfileStatus.message = this._utilService.extractErrorMessage(error);
        this.loadingProfileStatus.success = false;
      });
  };

  public confirmDeleteProfileStatus(id: number): void {
    this.deleting = false;
    this.deleteError = '';
    this._profileStatusToDelete = id;
    this.showDeleteModal = true;
  }

  public deleteProfileStatus = (): void => {
    this.deleting = true;
    this.deleteError = '';

    this._profileStatusService
      .deleteProfileStatus(this._profileStatusToDelete, '')
      .then(() => {
        this.deleting = false;
        this.showDeleteModal = false;
        this.loadProfileStatuses(this.searchParams);
      })
      .catch(error => {
        this.deleting = false;
        this.deleteError = this._utilService.extractErrorMessage(error);
      });
  };

  public getAllButOneProfileStatus() {
    return this.profileStatuses.filter((v, i, a) => v.ID != this._profileStatusToDelete);
  }

  public deleteMigrateProfileStatus = (): void => {
    this.deleting = true;
    this.deleteError = '';
    if (this.profileStatusToMigrateTo == 'None') {
      this.profileStatusToMigrateTo = '';
    }
    this._profileStatusService
      .deleteProfileStatus(this._profileStatusToDelete, this.profileStatusToMigrateTo)
      .then(() => {
        this.deleting = false;
        this.showDeleteModal = false;
        this.loadProfileStatuses(this.searchParams);
      })
      .catch(error => {
        this.deleting = false;
        this.deleteError = this._utilService.extractErrorMessage(error);
      });
  };

  public isClassTypeEmpty = (): boolean => {
    return this.selectedProfileStatus.MemberType1.trim() == '';
  };
}
